import { ref } from 'vue';
import { defineStore } from 'pinia';
import { recruitGetSimilarJobs } from '@/api/recruit';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { debounce } from 'lodash-es';

export const useSimilarJobs = defineStore('documentSimilarJobs', () => {
  const { addErrorSnackbar, addInfoSnackbar } = useAppSnackbar();

  // State
  const jobs = ref([]);
  const isLoading = ref(false);
  const currentPage = ref(0);
  const jobId = ref(null);
  const scope = ref('organization');
  const locality = ref('local');
  const textFilter = ref('');
  const showSeeMore = ref(true);

  // Methods
  const fetchJobs = async ({ reset = false } = {}) => {
    if (reset) {
      currentPage.value = 0;
      jobs.value = [];
    }

    isLoading.value = true;

    try {
      const { documents, pagination } = await recruitGetSimilarJobs({
        jobId: jobId.value,
        scope: scope.value,
        locality: locality.value,
        textFilter: textFilter.value,
        page: currentPage.value,
      });

      if (reset) {
        jobs.value = documents;
      } else {
        jobs.value = [...jobs.value, ...documents];
      }

      showSeeMore.value = pagination.page < pagination.totalPages;
    } catch (error) {
      addErrorSnackbar({
        text: `Failed to load similar jobs: ${error.message}`,
      });
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  // used to debounce search text changes
  const fetchJobsDebounced = debounce(fetchJobs, 500);
  // used to debounce scope and locality changes that happen at the same time during a search upgrade
  const fetchJobsDebouncedShort = debounce(fetchJobs, 100);

  const initialize = (newJobId) => {
    if (newJobId === jobId.value) return null;
    jobId.value = newJobId;
    return fetchJobs({ reset: true });
  };

  const handleSearch = (searchText) => {
    if (searchText === textFilter.value) return null;
    textFilter.value = searchText;
    return fetchJobsDebounced({ reset: true });
  };

  const handleLocalityChange = (newLocality) => {
    if (newLocality === locality.value) return null;
    locality.value = newLocality;
    return fetchJobsDebouncedShort({ reset: true });
  };

  const handleScopeChange = (newScope) => {
    if (newScope === scope.value) return null;
    scope.value = newScope;
    return fetchJobsDebouncedShort({ reset: true });
  };

  const loadMore = () => {
    currentPage.value += 1;
    return fetchJobs({ reset: false });
  };

  const onSearchUpgrade = (text) => {
    addInfoSnackbar({ text, timeout: 6000 });
  };

  const onSearchUpgradeDebounced = debounce(onSearchUpgrade, 2000);

  const handleSearchUpgrade = (text) => {
    onSearchUpgradeDebounced(text);
  };

  const reset = () => {
    jobs.value = [];
    isLoading.value = false;
    currentPage.value = 0;
    jobId.value = null;
    scope.value = 'organization';
    locality.value = 'local';
    textFilter.value = '';
  };

  return {
    // State
    jobs,
    isLoading,
    currentPage,
    scope,
    locality,
    textFilter,
    showSeeMore,

    // Methods
    initialize,
    handleSearch,
    handleLocalityChange,
    handleScopeChange,
    loadMore,
    handleSearchUpgrade,
    reset,
  };
});
